import moment from 'moment';
import React from 'react';
import {date} from 'yup';
import {Box, Typography} from '@mui/material';
import {capitalize} from 'lodash';
import {ActionType} from '../../../components/AutoForm/AutoFieldNew';
import {WizardField} from '../../../components/Wizard/QuestionWrapper';
import {ReviewDetailsFragment} from '../../../schema-types/ReviewDetailsFragment';
import {hasSpecificAnswer} from '../../../components/Wizard/operators';
import {CommunicationMethod, ReviewAction} from '../../../schema-types/globalTypes';
import {newUTCDate} from '../../../utils/newUTCDate';
import {precursorAndHeaderFieldsType} from './precursorFields';
import {postcursorFieldsType} from '../../PostcursorPage/postcursorFields';

export const communicationMethodOptions = [
  {
    label: 'Post',
    value: CommunicationMethod.POST,
  },
  {
    label: 'Face to face',
    value: CommunicationMethod.FACE_TO_FACE,
  },
  {
    label: 'Telephone',
    value: CommunicationMethod.TELEPHONE,
  },
  {
    label: 'Email',
    value: CommunicationMethod.EMAIL,
  },
];

const CommunicationAttemptQuestion: React.FC<{attempt: string}> = ({attempt}) => {
  return (
    <>
      <Typography mb={2}>
        Note: You must try and contact the client 3 times at least a month before the
        review is due. This should consist of at least 2 phone calls and 1 email.
      </Typography>
      <Typography variant="h4">{capitalize(attempt)} contact attempt date:</Typography>
    </>
  );
};

const ReviewActionQuestion: React.FC<{reviewDetails?: ReviewDetailsFragment}> = ({
  reviewDetails,
}) => {
  if (!reviewDetails?.clientCommunicationAttempts?.length) {
    return null;
  }
  return (
    <>
      <Typography>
        <strong>Previous communication attempts have been made for this review:</strong>
      </Typography>
      {reviewDetails.clientCommunicationAttempts.map((x, index) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Box mt={1} key={index}>
            Attempt #{index + 1}was made{' '}
            {x.contactDate ? moment(x.contactDate).format('DD/MM/yyyy') : ''}by{' '}
            {x.contactMethod
              ? communicationMethodOptions.find(
                  (methodOption) => methodOption.value === x.contactMethod
                )?.label
              : 'unknown'}
          </Box>
        );
      })}
    </>
  );
};

export const skippingAnnualReview = (
  values: precursorAndHeaderFieldsType | postcursorFieldsType,
  reviewDetails?: ReviewDetailsFragment
): boolean => {
  if (reviewDetails?.reviewReason === 'Ongoing client service') {
    // only show in precursor
    if ('reviewAction' in values && values.reviewAction) {
      if (
        values.reviewAction === ReviewAction.CLIENT_DECLINED ||
        values.reviewAction === ReviewAction.NO_RESPONSE
      ) {
        return true;
      }
    }
  }
  return false;
};

/**
 * using this array to control the total number of attempts
 * so each one has its own ordinal
 */
export const numbersToPhrasing = [
  'first',
  'second',
  'third',
  // 'fourth', 'fifth'
];

export const reviewActionFields = (
  reviewDetails?: ReviewDetailsFragment
): WizardField<precursorAndHeaderFieldsType>[] => {
  const uncontactableAttempts = reviewDetails?.clientCommunicationAttempts?.length || 0;

  const noResponseEnabled = hasSpecificAnswer<precursorAndHeaderFieldsType>(
    'reviewAction',
    ReviewAction.NO_RESPONSE
  );

  const declinedEnabled = hasSpecificAnswer<precursorAndHeaderFieldsType>(
    'reviewAction',
    ReviewAction.CLIENT_DECLINED
  );

  const maxPastDateValidation = date().max(
    moment.utc(newUTCDate()).endOf('day').toDate(),
    'Date must be in the past'
  );

  const noResponseAttemptFields: WizardField<precursorAndHeaderFieldsType>[] =
    numbersToPhrasing.flatMap((attemptPhrase, i) => {
      const attemptTitle = `No response from client: ${attemptPhrase} attempt`;
      const attemptData = reviewDetails?.clientCommunicationAttempts?.[i];

      return [
        {
          id: `noResponseDate_${i + 1}`,
          title: attemptTitle,
          questionComponent: <CommunicationAttemptQuestion attempt={attemptPhrase} />,
          label: '',
          enabled: noResponseEnabled,
          type: ActionType.datepicker,
          requireSubmit: true,
          initialValue: attemptData?.contactDate,
          validation: maxPastDateValidation,
        } as unknown as WizardField<precursorAndHeaderFieldsType>,

        {
          id: `noResponseMethod_${i + 1}`,
          title: attemptTitle,
          question: 'Method of communication',
          label: '',
          enabled: noResponseEnabled,
          type: ActionType.buttonGroup,
          requireSubmit: true,
          options: communicationMethodOptions,
          initialValue: attemptData?.contactMethod || undefined,
        } as unknown as WizardField<precursorAndHeaderFieldsType>,
      ];
    });

  return [
    {
      id: 'reviewAction',
      title: 'Review Action',
      questionComponent: <ReviewActionQuestion reviewDetails={reviewDetails} />,
      label: '',
      enabled: () => reviewDetails?.reviewReason === 'Ongoing client service', // annual reviews only
      initialValue: reviewDetails?.reviewAction
        ? String(reviewDetails?.reviewAction)
        : undefined,
      type: ActionType.buttonGroup,
      requireSubmit: true,
      options: [
        {
          label: 'Review Meeting',
          value: ReviewAction.REVIEW_MEETING,
        },
        {
          label: 'Declined by client',
          value: ReviewAction.CLIENT_DECLINED,
        },
        {
          label: `No response from client${
            uncontactableAttempts ? `: attempt #${uncontactableAttempts + 1}` : ''
          }`,
          value: ReviewAction.NO_RESPONSE,
        },
      ],
    },
    {
      id: 'declinedByClientDate',
      title: 'Declined by client date',
      question: 'When did the client decline the meeting?',
      label: '',
      enabled: declinedEnabled,
      type: ActionType.datepicker,
      alwaysShowCalendar: true,
      requireSubmit: true,
      validation: maxPastDateValidation,
    },
    {
      id: 'declinedByClientMethod',
      title: 'Declined communication',
      question: 'Method of communication',
      label: '',
      enabled: declinedEnabled,
      type: ActionType.buttonGroup,
      requireSubmit: true,
      options: communicationMethodOptions,
    },
    {
      id: 'declineComment',
      title: 'Decline Comment',
      question: 'Why did the client decline? (optional)',
      label: '',
      enabled: declinedEnabled,
      type: ActionType.text,
      multiline: true,
      requireSubmit: true,
      optional: true,
      charLimit: 200,
    },
    ...noResponseAttemptFields,
  ];
};
